import React from 'react';
import { PageHeader } from '../components/ui/PageHeader';
import { SectionHeading } from '../components/ui/SectionHeading';
import { StatCard } from '../components/StatCard';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Award, Download, CheckCircle, Star, Target, ShieldAlert, TrendingUp, BarChart } from 'lucide-react';
import { FORMATIONS } from '../data/constants';
function NationalIndicators({
  data
}: {
  data: NonNullable<(typeof FORMATIONS)[number]['inserJeunes']>;
}) {
  return <div className="mt-10 bg-slate-50 border border-slate-100 rounded-3xl p-8" data-id="element-2070">
      <h4 className="flex items-center justify-center gap-2 text-lg font-bold text-primary-dark mb-8" data-id="element-2071">
        <BarChart className="w-5 h-5 text-primary" data-id="element-2072" />
        Indicateurs nationaux (InserJeunes)
      </h4>
      <div className="grid sm:grid-cols-2 gap-6" data-id="element-2073">
        <div className="bg-white rounded-2xl p-6 shadow-sm" data-id="element-2074">
          <div className="text-4xl font-extrabold text-brand-orange mb-2 font-heading" data-id="element-2075">
            {data.emploi6Mois}
          </div>
          <p className="font-medium text-primary-dark" data-id="element-2076">
            apprenants sortants en emploi salarié 6 mois après leur sortie de
            formation
          </p>
        </div>
        <div className="bg-white rounded-2xl p-6 shadow-sm flex flex-col justify-center" data-id="element-2077">
          <div className="flex justify-between mb-3 text-sm" data-id="element-2078">
            <span className="font-bold text-primary-dark" data-id="element-2079">
              {data.poursuiteEtudes} poursuivent
            </span>
            <span className="font-bold text-primary-dark" data-id="element-2080">
              {data.sortieFormation} sortent
            </span>
          </div>
          <div className="h-4 w-full bg-slate-200 rounded-full overflow-hidden flex" data-id="element-2081">
            <div className="h-full bg-primary" style={{
            width: data.poursuiteEtudes
          }} data-id="element-2082" />
            <div className="h-full bg-brand-orange" style={{
            width: data.sortieFormation
          }} data-id="element-2083" />
          </div>
        </div>
        <div className="bg-white rounded-2xl p-6 shadow-sm text-center" data-id="element-2084">
          <h5 className="font-bold text-primary-dark mb-3" data-id="element-2085">
            Rémunération 1 an après
          </h5>
          <div className="text-2xl font-extrabold text-primary mb-1 font-heading" data-id="element-2086">
            Entre {data.remuneration}
          </div>
          <p className="text-sm text-muted-foreground" data-id="element-2087">pour 50% des salariés</p>
        </div>
        <div className="bg-white rounded-2xl p-6 shadow-sm flex flex-col justify-center" data-id="element-2088">
          <div className="text-3xl font-extrabold text-primary-dark mb-2 font-heading" data-id="element-2089">
            {data.rupture}
          </div>
          <p className="font-medium text-primary-dark" data-id="element-2090">
            Part des contrats interrompus avant leur terme
          </p>
        </div>
      </div>
      <p className="text-xs text-muted-foreground mt-6 text-center" data-id="element-2091">
        Source : InserJeunes (Ministère de l'Éducation nationale et de la
        Jeunesse). Moyennes nationales à titre indicatif.
      </p>
    </div>;
}
export function Qualite() {
  const tp = FORMATIONS.find(f => f.id === 'tp-conseiller-vente');
  const bts = FORMATIONS.find(f => f.id === 'bts-ndrc');
  return <div className="flex flex-col w-full" data-id="element-2092">
      <PageHeader title="Qualité et Transparence" subtitle="Zillenium Alternance s'engage dans une démarche d'amélioration continue pour garantir l'excellence de ses formations." data-id="element-2093" />

      {/* Intro */}
      <section className="py-16 md:py-24 bg-white" data-id="element-2094">
        <div className="container max-w-4xl mx-auto text-center" data-id="element-2095">
          <p className="text-xl md:text-2xl text-muted-foreground leading-relaxed font-medium" data-id="element-2096">
            Notre démarche qualité place l'apprenant et l'entreprise au cœur de
            nos préoccupations. Nous mesurons régulièrement nos performances
            afin d'adapter nos méthodes pédagogiques et de répondre aux
            exigences du référentiel national qualité.
          </p>
        </div>
      </section>

      {/* Certificats Qualiopi */}
      <section className="py-24 bg-muted relative overflow-hidden" data-id="element-2097">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-brand-orange rounded-full blur-[100px] opacity-10 pointer-events-none" data-id="element-2098" />
        <div className="container max-w-5xl mx-auto relative z-10" data-id="element-2099">
          <SectionHeading title="Nos certifications Qualiopi" centered data-id="element-2100" />
          <div className="grid md:grid-cols-2 gap-10 mt-16" data-id="element-2101">
            <Card className="border-none shadow-xl hover:-translate-y-2 transition-all duration-300 bg-white group" data-id="element-2102">
              <div className="h-2 w-full bg-primary" data-id="element-2103" />
              <CardHeader className="pt-8 pb-4" data-id="element-2104">
                <div className="w-16 h-16 bg-lavande rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300" data-id="element-2105">
                  <Award className="w-8 h-8 text-primary animate-soft-pulse" data-id="element-2106" />
                </div>
                <CardTitle className="text-2xl font-extrabold font-heading text-primary-dark" data-id="element-2107">
                  Formation par apprentissage
                </CardTitle>
              </CardHeader>
              <CardContent data-id="element-2108">
                <p className="text-lg text-muted-foreground mb-8" data-id="element-2109">
                  Certifié par CAPCERT. Valable jusqu'au 30/10/2027.
                </p>
                <Button variant="outline" className="w-full border-primary/20 text-primary-dark hover:bg-primary/5 font-semibold" asChild data-id="element-2110">
                  <a href="https://cdn.magicpatterns.com/uploads/uxuWAaU2vHtjX7ZhfSq9Aw/Capture_d’ecran_2026-06-21_a_19.37.57.png" target="_blank" rel="noopener noreferrer" data-id="element-2111">
                    <Download className="w-5 h-5 mr-2" data-id="element-2112" />
                    Télécharger le certificat
                  </a>
                </Button>
              </CardContent>
            </Card>

            <Card className="border-none shadow-xl hover:-translate-y-2 transition-all duration-300 bg-white group" data-id="element-2113">
              <div className="h-2 w-full bg-brand-orange" data-id="element-2114" />
              <CardHeader className="pt-8 pb-4" data-id="element-2115">
                <div className="w-16 h-16 bg-lavande rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300" data-id="element-2116">
                  <Award className="w-8 h-8 text-brand-orange animate-soft-pulse" data-id="element-2117" />
                </div>
                <CardTitle className="text-2xl font-extrabold font-heading text-primary-dark" data-id="element-2118">
                  Actions de formation
                </CardTitle>
              </CardHeader>
              <CardContent data-id="element-2119">
                <p className="text-lg text-muted-foreground mb-8" data-id="element-2120">
                  Certifié par CAPCERT. Valable jusqu'au 01/09/2027.
                </p>
                <Button variant="outline" className="w-full border-primary/20 text-primary-dark hover:bg-primary/5 font-semibold" asChild data-id="element-2121">
                  <a href="https://cdn.magicpatterns.com/uploads/2eVnp5Bk4nD8NeVbW5n73A/Capture_d’ecran_2026-06-21_a_19.38.12.png" target="_blank" rel="noopener noreferrer" data-id="element-2122">
                    <Download className="w-5 h-5 mr-2" data-id="element-2123" />
                    Télécharger le certificat
                  </a>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Indicateurs de résultats */}
      <section id="indicateurs" className="py-24 bg-white relative overflow-hidden" data-id="element-2124">
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-primary rounded-full blur-[100px] opacity-10 pointer-events-none" data-id="element-2125" />
        <div className="container max-w-6xl mx-auto relative z-10" data-id="element-2126">
          <SectionHeading title="Nos indicateurs de résultats réels" centered data-id="element-2127" />

          <div className="space-y-20 mt-16" data-id="element-2128">
            <div data-id="element-2129">
              <h3 className="text-3xl font-extrabold font-heading mb-10 text-center text-primary-dark" data-id="element-2130">
                TP Conseiller de Vente{' '}
                <span className="text-primary font-medium text-2xl block mt-2" data-id="element-2131">
                  (Session 2024/2025)
                </span>
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-2132">
                <StatCard value="100%" label="Réussite" icon={<CheckCircle className="w-8 h-8" data-id="element-2134" />} data-id="element-2133" />
                <StatCard value="97%" label="Satisfaction" icon={<Star className="w-8 h-8" data-id="element-2136" />} data-id="element-2135" />
                <StatCard value="60%" label="Présentation examen" icon={<Target className="w-8 h-8" data-id="element-2138" />} data-id="element-2137" />
                <StatCard value="40%" label="Rupture" icon={<ShieldAlert className="w-8 h-8" data-id="element-2140" />} data-id="element-2139" />
              </div>
              <p className="text-center text-lg text-muted-foreground mt-8 font-medium" data-id="element-2141">
                Suites de parcours : 60% poursuite d'études / 40% emploi
              </p>
              {tp?.inserJeunes && <NationalIndicators data={tp.inserJeunes} data-id="element-2142" />}
            </div>

            <div className="max-w-4xl mx-auto" data-id="element-2143">
              <h3 className="text-3xl font-extrabold font-heading mb-10 text-center text-primary-dark" data-id="element-2144">
                BTS NDRC{' '}
                <span className="text-primary font-medium text-2xl block mt-2" data-id="element-2145">
                  (Enquête mai 2026)
                </span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2146">
                <StatCard value="94%" label="Satisfaction globale" icon={<TrendingUp className="w-8 h-8" data-id="element-2148" />} data-id="element-2147" />
                <Card className="bg-muted border-none flex items-center justify-center p-8 text-center shadow-inner" data-id="element-2149">
                  <span className="text-lg text-muted-foreground font-medium" data-id="element-2150">
                    Résultats aux examens attendus en 2027
                  </span>
                </Card>
              </div>
              {bts?.inserJeunes && <NationalIndicators data={bts.inserJeunes} data-id="element-2151" />}
            </div>
          </div>
        </div>
      </section>
    </div>;
}